"use client";

import { Button } from "@/components/atoms/Button";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import { ArrowDown, ArrowUpRight, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

/**
 * One flat metadata row = a single subject.
 *
 * - Normal subject: belongs to a semester tab via `semester_name`; shows `credits`.
 * - Elective subject: has `elective_name` (the specialization chip) and lives under
 *   the synthetic "Electives" tab; shows its own `subject_semester` instead of credits.
 */
export interface ProgramStructure4SubjectRow {
  id?: string;
  semester_name?: string;
  semester_subtitle?: string;
  subject_name?: string;
  subject_image?: string;
  subject_image_alt?: string;
  credits?: string;
  elective_name?: string;
  subject_semester?: string;
  priority?: number;
  status?: boolean;
}

export interface ProgramStructure4Payload {
  status?: boolean;
  section_background_image?: string;
  section_background_image_alt?: string;
  section_overlay_rgba?: string;
  headline_primary?: string;
  headline_primary_color?: string;
  headline_accent?: string;
  headline_accent_color?: string;
  topics_column_title?: string;
  subjects_column_label?: string;
  credits_column_label?: string;
  electives_tab_label?: string;
  electives_semester_label?: string;
  electives_empty_message?: string;
  card_background_color?: string;
  module_list_track_background_color?: string;
  content_panel_background_color?: string;
  module_inactive_border_color?: string;
  module_list_divider_color?: string;
  module_active_background_color?: string;
  module_active_text_color?: string;
  module_inactive_text_color?: string;
  section_background_color?: string;
  cta_button_text?: string;
  cta_button_text_color?: string;
  cta_button_color?: string;
  brochure_cta_text?: string;
  brochure_cta_text_color?: string;
  brochure_cta_border_color?: string;
  cta_button_is_cta?: boolean;
  brochure_cta_is_cta?: boolean;
  metadata?: ProgramStructure4SubjectRow[];
}

interface ProgramStructure4Props {
  data: ProgramStructure4Payload | { data: ProgramStructure4Payload };
}

/** A single subject row. `right` is the trailing value — credits, or (for electives) its semester. */
interface SubjectItem {
  name: string;
  right: string;
  image: string;
  imageAlt: string;
}

/** One selectable elective (specialization chip) and the subjects it unlocks. */
interface ElectiveGroup {
  name: string;
  priority: number;
  subjects: SubjectItem[];
}

interface SemesterTab {
  name: string;
  subtitle: string;
  priority: number;
  /** True for the synthetic electives tab: render `electives` chips instead of `subjects`. */
  isElectives: boolean;
  subjects: SubjectItem[];
  electives: ElectiveGroup[];
}

function resolvePayload(
  raw: ProgramStructure4Props["data"],
): ProgramStructure4Payload {
  const anyData = raw as { data?: ProgramStructure4Payload };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data;
  }
  return (raw as ProgramStructure4Payload) || {};
}

/**
 * Render a headline that uses `{ }` markers to highlight words in the accent
 * color: `Program {Structure}` colors "Structure" with `highlightColor`.
 * `{{...}}` highlights the wrapped words and pushes them onto their own line.
 */
function renderHeadline(text: string, highlightColor: string): ReactNode {
  if (!text) return null;
  const re = /\{\{([^}]+)\}\}|\{([^}]+)\}/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const isBlock = m[1] != null;
    nodes.push(
      <span key={`hl-${key++}`} style={{ color: highlightColor }}>
        {isBlock ? m[1] : m[2]}
      </span>,
    );
    if (isBlock) nodes.push(<br key={`br-${key++}`} />);
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/**
 * Group flat subject rows into ordered tabs.
 *
 * Rows with an `elective_name` are collected into specialization groups and
 * surfaced under one synthetic electives tab (appended after the semesters).
 * All other rows group into semester tabs by `semester_name`.
 */
function buildTabs(
  rows: ProgramStructure4SubjectRow[],
  electivesTabLabel: string,
): SemesterTab[] {
  const byName = new Map<string, SemesterTab>();
  const byElective = new Map<string, ElectiveGroup>();
  let electivePriority = Number.POSITIVE_INFINITY;

  rows
    .filter(
      (r) =>
        r &&
        r.status !== false &&
        String(r.subject_name ?? "").trim().length > 0,
    )
    .slice()
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
    .forEach((r) => {
      const priority = Number(r.priority) || 0;
      const subjectName = String(r.subject_name).trim();
      const electiveName = String(r.elective_name ?? "").trim();

      if (electiveName) {
        if (!byElective.has(electiveName)) {
          byElective.set(electiveName, {
            name: electiveName,
            priority,
            subjects: [],
          });
        }
        byElective.get(electiveName)!.subjects.push({
          name: subjectName,
          right: String(r.subject_semester ?? "").trim(),
          image: String(r.subject_image ?? "").trim(),
          imageAlt: String(r.subject_image_alt ?? "").trim(),
        });
        electivePriority = Math.min(electivePriority, priority);
        return;
      }

      const name = String(r.semester_name ?? "").trim();
      if (!name) return;
      if (!byName.has(name)) {
        byName.set(name, {
          name,
          subtitle: String(r.semester_subtitle ?? "").trim(),
          priority,
          isElectives: false,
          subjects: [],
          electives: [],
        });
      }
      const tab = byName.get(name)!;
      // First non-empty subtitle row wins for the whole semester.
      if (!tab.subtitle) {
        tab.subtitle = String(r.semester_subtitle ?? "").trim();
      }
      tab.subjects.push({
        name: subjectName,
        right: String(r.credits ?? "").trim(),
        image: String(r.subject_image ?? "").trim(),
        imageAlt: String(r.subject_image_alt ?? "").trim(),
      });
    });

  const tabs = Array.from(byName.values()).sort(
    (a, b) => a.priority - b.priority,
  );

  const electives = Array.from(byElective.values()).sort(
    (a, b) => a.priority - b.priority,
  );
  if (electives.length > 0) {
    tabs.push({
      name: electivesTabLabel || "Electives",
      subtitle: "",
      priority: electivePriority,
      isElectives: true,
      subjects: [],
      electives,
    });
  }

  return tabs;
}

function SubjectsBody({
  columnTitle,
  subjectsLabel,
  rightLabel,
  subjects,
  showRight,
  panelBg,
  dividerColor,
  emptyMessage = "",
}: {
  columnTitle: string;
  subjectsLabel: string;
  rightLabel: string;
  subjects: SubjectItem[];
  showRight: boolean;
  panelBg: string;
  dividerColor: string;
  emptyMessage?: string;
}) {
  return (
    <div className="flex flex-col gap-3 w-full">
      {columnTitle ? (
        <p className="min-[1024px]:text-lg text-base font-semibold text-gray-900 leading-snug pt-2 min-[1024px]:pt-0 px-1 min-[1024px]:px-0">
          {columnTitle}
        </p>
      ) : null}
      <div
        className="flex flex-col gap-2 p-4 min-[1024px]:p-[18px] rounded-2xl"
        style={{ backgroundColor: panelBg }}
      >
        {subjects.length === 0 ? (
          <p className="text-sm min-[1024px]:text-[15px] text-gray-400 leading-snug py-6 w-full text-center">
            {emptyMessage}
          </p>
        ) : (
          <>
            {subjectsLabel || (showRight && rightLabel) ? (
              <div className="flex items-center justify-between gap-4 pb-2">
                <p className="text-base min-[1024px]:text-lg font-semibold text-gray-900 leading-snug min-w-0 flex-1">
                  {subjectsLabel}
                </p>
                {showRight && rightLabel ? (
                  <p className="text-base min-[1024px]:text-lg font-semibold text-gray-900 leading-snug whitespace-nowrap shrink-0">
                    {rightLabel}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-col overflow-y-auto scrollbar-hide max-h-[264px] min-[1024px]:max-h-[300px]">
              {subjects.map((s, i) => (
                <div
                  key={`${s.name}-${i}`}
                  className={cn(
                    "flex items-center justify-between gap-4 py-2",
                    i < subjects.length - 1 && "border-b border-solid",
                  )}
                  style={
                    i < subjects.length - 1
                      ? { borderBottomColor: dividerColor }
                      : undefined
                  }
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <p className="text-sm min-[1024px]:text-[15px] text-gray-800 leading-snug min-w-0 flex-1">
                      {s.name}
                    </p>
                    {s.image ? (
                      <Image
                        src={s.image}
                        alt={s.imageAlt || s.name}
                        width={48}
                        height={42}
                        className="h-8 w-8 shrink-0 object-contain"
                      />
                    ) : null}
                  </div>
                  {showRight && s.right ? (
                    <span className="text-sm min-[1024px]:text-[15px] font-semibold text-gray-900 whitespace-nowrap shrink-0">
                      {s.right}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Electives tab: a row of selectable specialization chips + the chosen one's subjects. */
function ElectivePanel({
  columnTitle,
  subjectsLabel,
  semesterLabel,
  electives,
  selectedName,
  onSelect,
  panelBg,
  dividerColor,
  chipActiveBg,
  chipActiveText,
  chipInactiveText,
  chipBorder,
  emptyMessage,
}: {
  columnTitle: string;
  subjectsLabel: string;
  semesterLabel: string;
  electives: ElectiveGroup[];
  selectedName: string;
  onSelect: (name: string) => void;
  panelBg: string;
  dividerColor: string;
  chipActiveBg: string;
  chipActiveText: string;
  chipInactiveText: string;
  chipBorder: string;
  emptyMessage: string;
}) {
  const selected =
    electives.find((e) => e.name === selectedName) || electives[0];
  const showRight = !!selected?.subjects.some((s) => s.right);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-wrap gap-2 min-[1024px]:gap-3 max-h-[244px] overflow-y-auto scrollbar-hide">
        {electives.map((e) => {
          const isSel = e.name === selected?.name;
          return (
            <button
              key={e.name}
              type="button"
              onClick={() => onSelect(e.name)}
              className="rounded-md border border-solid px-3 py-2 text-xs min-[1024px]:text-sm font-semibold transition-colors"
              style={{
                backgroundColor: isSel ? chipActiveBg : "transparent",
                color: isSel ? chipActiveText : chipInactiveText,
                borderColor: isSel ? "transparent" : chipBorder,
              }}
            >
              {e.name}
            </button>
          );
        })}
      </div>
      <SubjectsBody
        columnTitle={columnTitle}
        subjectsLabel={subjectsLabel}
        rightLabel={semesterLabel}
        subjects={selected?.subjects || []}
        showRight={showRight}
        panelBg={panelBg}
        dividerColor={dividerColor}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}

const ProgramStructure4: FC<ProgramStructure4Props & { className?: string }> = ({
  data,
  className,
}) => {
  const payload = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );
  const prevTabIdx = useRef(0);

  const {
    section_background_image: bgImage = "",
    section_background_image_alt: bgAlt = "",
    section_overlay_rgba: overlayRgba = "",
    headline_primary = "Program Structure",
    headline_primary_color = "#FFFFFF",
    headline_accent = "",
    headline_accent_color = "#F5C542",
    topics_column_title = "",
    subjects_column_label = "Subjects",
    credits_column_label = "Credits",
    electives_tab_label = "Elective Courses",
    electives_semester_label = "Semester",
    electives_empty_message = "There are no subjects in this elective",
    card_background_color = "#FFFFFF",
    module_list_track_background_color = "#E8EAED",
    content_panel_background_color = "#F3F4F6",
    module_inactive_border_color = "#E5E7EB",
    module_list_divider_color = "#94A3B8",
    module_active_background_color = "#FF6B00",
    module_active_text_color = "#FFFFFF",
    module_inactive_text_color = "#111827",
    section_background_color = "#0B0F14",
    cta_button_text = "Apply Now",
    cta_button_text_color = "#FFFFFF",
    cta_button_color = "#FF6B00",
    brochure_cta_text = "Download Brochure",
    brochure_cta_text_color = "#FFFFFF",
    brochure_cta_border_color = "#FFFFFF",
  } = payload;

  const payloadRaw = payload as Record<string, unknown>;
  const ctaButtonIsCta = isCmsShowFlagOn(
    payloadRaw?.cta_button_is_cta ?? payloadRaw?.ctaButtonIsCta,
    true,
  );
  const brochureCtaIsCta = isCmsShowFlagOn(
    payloadRaw?.brochure_cta_is_cta ?? payloadRaw?.brochureCtaIsCta,
    true,
  );

  const brochureTrim = (brochureUrl || "").trim();
  const brochureHref = brochureTrim ? normalizeHref(brochureTrim) : "";
  const primaryOpensLeadPopup = ctaButtonIsCta || !brochureHref;
  const brochureOpensLeadPopup = brochureCtaIsCta || !brochureHref;

  const tabs = useMemo(
    () =>
      buildTabs(
        Array.isArray(payload.metadata) ? payload.metadata : [],
        electives_tab_label,
      ),
    [payload.metadata, electives_tab_label],
  );

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [selectedElective, setSelectedElective] = useState("");
  /** Mobile only: whether the semester dropdown under the bar is expanded. */
  const [semesterListOpen, setSemesterListOpen] = useState(false);

  useEffect(() => {
    if (selectedIdx >= tabs.length) {
      setSelectedIdx(Math.max(0, tabs.length - 1));
    }
  }, [tabs.length, selectedIdx]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_structure_section");
  //   }
  // }, []);

  const active = tabs[selectedIdx];

  const onSelectTab = useCallback(
    (idx: number) => {
      if (
        prevTabIdx.current !== idx &&
        prevTabIdx.current !== 0 &&
        tabs[idx]
      ) {
        tabSwitched({
          section: "program_structure_section",
          component: "semester_tabs",
          label: tabs[idx].name || `semester_${idx}`,
        });
      }
      prevTabIdx.current = idx;
      setSelectedIdx(idx);
    },
    [tabs],
  );

  const onSelectElective = useCallback((name: string) => {
    tabSwitched({
      section: "program_structure_section",
      component: "elective_chips",
      label: name,
    });
    setSelectedElective(name);
  }, []);

  const onCta = useCallback(() => {
    leadCtaClick({
      section: "program_structure_section",
      component: "cta_button",
      label: cta_button_text,
    });
    // Apply Now is a plain lead capture — REGISTER keeps the brochure
    // auto-download disarmed. Only the brochure CTA below uses DOWNLOAD.
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch, cta_button_text]);

  const brochureLabel = (brochure_cta_text || "").trim();
  const onDownloadBrochure = useCallback(() => {
    if (!brochureLabel) return;
    leadCtaClick({
      section: "program_structure_section",
      component: "brochure_cta",
      label: brochureLabel,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureUrl,
      }),
    );
  }, [brochureLabel, dispatch, brochureUrl]);

  if (payload.status === false) return null;

  if (tabs.length === 0) return null;

  const bgSrc = (bgImage || "").trim();
  const overlayTrim = (overlayRgba || "").trim();
  const tabHasCredits = (t?: SemesterTab) =>
    !!t && t.subjects.some((s) => s.right.length > 0);

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative flex w-full items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden",
        className,
      )}
      style={{
        backgroundColor: section_background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {bgSrc ? (
        <>
          <Image
            src={bgSrc}
            alt={bgAlt || ""}
            fill
            className="object-cover object-center"
            sizes="100vw"
          />
          {overlayTrim ? (
            <div
              className="absolute inset-0 z-[1] pointer-events-none"
              style={{ backgroundColor: overlayTrim }}
              aria-hidden
            />
          ) : null}
        </>
      ) : null}

      <div className="relative z-10 w-full flex flex-col items-center justify-center min-[1280px]:w-screen">
        <div
          className={cn(
            "w-full flex flex-col items-center justify-center gap-7 min-[1024px]:gap-10",
            "pt-[30px] pb-20 min-[1024px]:py-[70px] px-[30px]",
          )}
        >
          <div className="flex flex-col items-center w-full max-w-[312px] min-[1024px]:max-w-none justify-center gap-[30px]">
            <h2 className="text-center text-2xl min-[1024px]:text-[32px] font-bold leading-[126%] text-white max-w-4xl min-[1024px]:max-w-5xl">
              <span className="block" style={{ color: headline_primary_color }}>
                {renderHeadline(headline_primary, headline_accent_color)}
              </span>
              {headline_accent ? (
                <span
                  className="mt-1 block"
                  style={{ color: headline_accent_color }}
                >
                  {headline_accent}
                </span>
              ) : null}
            </h2>
            <div className="flex min-[1024px]:flex-row flex-col gap-2 items-center justify-center min-[1024px]:gap-4 w-full min-[1024px]:w-auto">
              {primaryOpensLeadPopup ? (
                <Button
                  type="button"
                  onClick={onCta}
                  className="rounded-md py-3 px-3 flex gap-2 items-center justify-center w-[140px] h-[45px] min-[1024px]:w-[150.56px] min-[1024px]:h-12 min-[1024px]:p-0 text-base min-[1024px]:text-lg font-semibold"
                  style={{
                    backgroundColor: cta_button_color,
                    color: cta_button_text_color,
                  }}
                >
                  {cta_button_text}
                  <ArrowUpRight className="size-5 shrink-0" aria-hidden />
                </Button>
              ) : (
                <Link
                  href={brochureHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md py-3 px-3 flex gap-2 items-center justify-center w-[140px] h-[45px] min-[1024px]:w-[150.56px] min-[1024px]:h-12 min-[1024px]:p-0 text-base min-[1024px]:text-lg font-semibold transition-opacity hover:opacity-95"
                  style={{
                    backgroundColor: cta_button_color,
                    color: cta_button_text_color,
                  }}
                  onClick={() =>
                    leadCtaClick({
                      section: "program_structure_section",
                      component: "cta_button_link",
                      label: cta_button_text,
                    })
                  }
                >
                  {cta_button_text}
                  <ArrowUpRight className="size-5 shrink-0" aria-hidden />
                </Link>
              )}
              {brochureLabel ? (
                brochureOpensLeadPopup ? (
                  <Button
                    type="button"
                    classType="ghost"
                    onClick={onDownloadBrochure}
                    className={cn(
                      "rounded-md border-2 border-solid bg-transparent py-3 px-[18px]",
                      "flex w-full items-center justify-center gap-2 min-[1024px]:w-[230px]",
                      "h-auto min-[1024px]:h-[50px] min-[1024px]:p-0 text-base font-semibold min-[1024px]:text-lg",
                      "transition-colors hover:bg-white/10",
                    )}
                    style={{
                      color: brochure_cta_text_color,
                      borderColor: brochure_cta_border_color,
                    }}
                  >
                    {brochureLabel}
                    <ArrowDown
                      className="size-5 shrink-0 stroke-[2.25]"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    />
                  </Button>
                ) : (
                  <Link
                    href={brochureHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "rounded-md border-2 border-solid bg-transparent py-3 px-[18px]",
                      "flex w-full items-center justify-center gap-2 min-[1024px]:w-[230px]",
                      "h-auto min-[1024px]:h-[50px] min-[1024px]:p-0 text-base font-semibold min-[1024px]:text-lg",
                      "transition-colors hover:bg-white/10",
                    )}
                    style={{
                      color: brochure_cta_text_color,
                      borderColor: brochure_cta_border_color,
                    }}
                    onClick={() =>
                      leadCtaClick({
                        section: "program_structure_section",
                        component: "brochure_cta_link",
                        label: brochureLabel,
                      })
                    }
                  >
                    {brochureLabel}
                    <ArrowDown
                      className="size-5 shrink-0 stroke-[2.25]"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    />
                  </Link>
                )
              ) : null}
            </div>
          </div>

          {/* Mobile: one dropdown bar for the active semester + a persistent content card */}
          <div className="min-[1024px]:hidden w-full flex flex-col gap-[10px]">
            <button
              type="button"
              onClick={() => setSemesterListOpen((open) => !open)}
              aria-expanded={semesterListOpen}
              aria-controls="ps4-semester-list"
              className="w-full flex items-center justify-between gap-3 rounded-lg p-2 text-left font-medium text-[18px] leading-[22px] transition-colors"
              style={{
                backgroundColor: module_active_background_color,
                color: module_active_text_color,
              }}
            >
              <span className="min-w-0 flex-1">{active?.name}</span>
              <ChevronRight
                className={cn(
                  "size-6 shrink-0 transition-transform text-current",
                  semesterListOpen ? "-rotate-90" : "rotate-90",
                )}
                aria-hidden
              />
            </button>

            {semesterListOpen ? (
              <div
                id="ps4-semester-list"
                className="w-full rounded-lg px-4 py-6"
                style={{ backgroundColor: card_background_color }}
              >
                {tabs.map((t, idx) => (
                  <div key={`${t.name}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectTab(idx);
                        setSemesterListOpen(false);
                      }}
                      className="w-full text-left font-medium text-[18px] leading-[22px] transition-colors"
                      style={{ color: module_inactive_text_color }}
                    >
                      {t.name}
                      {t.subtitle ? (
                        <span
                          className="mt-0.5 block font-normal text-[12px] leading-[100%]"
                          style={{ color: module_active_background_color }}
                        >
                          {t.subtitle}
                        </span>
                      ) : null}
                    </button>
                    {idx < tabs.length - 1 ? (
                      <div
                        className="my-[10px] border-b border-solid"
                        style={{ borderBottomColor: module_list_divider_color }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            <div
              className="w-full rounded-lg p-2"
              style={{ backgroundColor: card_background_color }}
            >
              {active?.isElectives ? (
                <ElectivePanel
                  columnTitle={topics_column_title}
                  subjectsLabel={subjects_column_label}
                  semesterLabel={electives_semester_label}
                  electives={active.electives}
                  selectedName={selectedElective}
                  onSelect={onSelectElective}
                  panelBg={content_panel_background_color}
                  dividerColor={module_inactive_border_color}
                  chipActiveBg={module_active_background_color}
                  chipActiveText={module_active_text_color}
                  chipInactiveText={module_inactive_text_color}
                  chipBorder={module_inactive_border_color}
                  emptyMessage={electives_empty_message}
                />
              ) : (
                <SubjectsBody
                  columnTitle={topics_column_title}
                  subjectsLabel={subjects_column_label}
                  rightLabel={credits_column_label}
                  subjects={active?.subjects || []}
                  showRight={tabHasCredits(active)}
                  panelBg={content_panel_background_color}
                  dividerColor={module_inactive_border_color}
                />
              )}
            </div>
          </div>

          {/* Desktop: two columns — semester tabs + subjects */}
          <div
            className="hidden min-[1024px]:flex gap-4 min-[1280px]:gap-11 w-full max-w-6xl rounded-lg mx-2 min-[1024px]:px-2 min-[1024px]:py-6 min-[1024px]:pl-8 min-[1024px]:pr-12"
            style={{ backgroundColor: card_background_color }}
          >
            {/* No min-height: the card is sized by the semester list, so it
                grows with extra semesters instead of padding out empty space. */}
            <div className="flex min-[1024px]:w-[min(440px,40vw)] min-[1280px]:w-[500px] flex-shrink-0 relative gap-2">
              <div
                className="flex-1 min-w-0 rounded-lg py-4 px-3 min-[1024px]:px-5"
                style={{ backgroundColor: module_list_track_background_color }}
              >
                <div className="flex flex-col gap-0 w-full min-w-0">
                  {tabs.map((t, idx) => {
                    const isSel = idx === selectedIdx;
                    const showRule = idx < tabs.length - 1;
                    return (
                      <div
                        key={`${t.name}-${idx}`}
                        className={cn(
                          "w-full min-w-0",
                          showRule &&
                            "border-b border-solid pb-4 mb-4 min-[1024px]:pb-5 min-[1024px]:mb-5",
                        )}
                        style={
                          showRule
                            ? { borderBottomColor: module_list_divider_color }
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          onClick={() => onSelectTab(idx)}
                          className="w-full min-[1024px]:p-2 flex items-center justify-between gap-4 rounded-lg cursor-pointer text-left transition-colors"
                          style={{
                            backgroundColor: isSel
                              ? module_active_background_color
                              : card_background_color,
                            color: isSel
                              ? module_active_text_color
                              : module_inactive_text_color,
                          }}
                        >
                          <span className="flex flex-col min-w-0 flex-1">
                            <span className="font-medium text-[16px] min-[1024px]:text-[18px] leading-[22px]">
                              {t.name}
                            </span>
                            {t.subtitle ? (
                              <span
                                className="text-[12px] leading-[100%] mt-0.5"
                                style={{
                                  color: isSel
                                    ? module_active_text_color
                                    : module_active_background_color,
                                }}
                              >
                                {t.subtitle}
                              </span>
                            ) : null}
                          </span>
                          <ChevronRight
                            className="size-6 shrink-0"
                            style={{
                              color: isSel
                                ? module_active_text_color
                                : module_inactive_text_color,
                            }}
                            aria-hidden
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 flex-1 min-w-0 min-[1024px]:w-[70%] justify-start">
              {active?.isElectives ? (
                <ElectivePanel
                  columnTitle={topics_column_title}
                  subjectsLabel={subjects_column_label}
                  semesterLabel={electives_semester_label}
                  electives={active.electives}
                  selectedName={selectedElective}
                  onSelect={onSelectElective}
                  panelBg={content_panel_background_color}
                  dividerColor={module_inactive_border_color}
                  chipActiveBg={module_active_background_color}
                  chipActiveText={module_active_text_color}
                  chipInactiveText={module_inactive_text_color}
                  chipBorder={module_inactive_border_color}
                  emptyMessage={electives_empty_message}
                />
              ) : (
                <SubjectsBody
                  columnTitle={topics_column_title}
                  subjectsLabel={subjects_column_label}
                  rightLabel={credits_column_label}
                  subjects={active?.subjects || []}
                  showRight={tabHasCredits(active)}
                  panelBg={content_panel_background_color}
                  dividerColor={module_inactive_border_color}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramStructure4;
